import { Check, ShoppingBag, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useRef } from 'react'
import { formatCurrency } from '../lib/money'
import type { Product } from '../types'

interface ProductQuickShopProps {
  readonly product: Product | null
  readonly quantityInCart: number
  readonly onAdd: (product: Product) => void
  readonly onClose: () => void
}

export function ProductQuickShop({
  product,
  quantityInCart,
  onAdd,
  onClose,
}: ProductQuickShopProps) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const open = product !== null
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (!first || !last) {
        event.preventDefault()
        closeRef.current?.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [open, onClose])

  return (
    <AnimatePresence>
      {product && (
        <div className="quick-shop-layer">
          <motion.button
            className="quick-shop-overlay"
            type="button"
            aria-label="Close quick shop"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0.12 : 0.2 }}
          />
          <motion.aside
            ref={dialogRef}
            className="quick-shop"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`quick-shop-title-${product.id}`}
            aria-describedby={`quick-shop-description-${product.id}`}
            data-reduced-motion={reduceMotion ? 'true' : 'false'}
            initial={
              reduceMotion
                ? { opacity: 0, x: 0 }
                : { opacity: 0, x: '100%' }
            }
            animate={{ opacity: 1, x: 0 }}
            exit={
              reduceMotion
                ? { opacity: 0, x: 0 }
                : { opacity: 0, x: '100%' }
            }
            transition={
              reduceMotion
                ? { duration: 0.12, ease: 'linear' }
                : { type: 'spring', damping: 30, stiffness: 290 }
            }
          >
            <div className="quick-shop__header">
              <div>
                <p>Quick shop</p>
                <span>Sub Zero favourite</span>
              </div>
              <button
                ref={closeRef}
                className="icon-button"
                type="button"
                aria-label="Close quick shop"
                onClick={onClose}
              >
                <X aria-hidden="true" />
              </button>
            </div>

            <div className="quick-shop__media">
              <img src={product.image} alt={product.alt} />
              {product.id === 'pick-mix' && (
                <img
                  className="quick-shop__bucket-logo"
                  src="/brand/sub-zero-logo-primary.png"
                  alt=""
                />
              )}
            </div>

            <div className="quick-shop__body">
              <p className="quick-shop__category">{product.category}</p>
              <h2 id={`quick-shop-title-${product.id}`}>{product.name}</h2>
              <p
                className="quick-shop__description"
                id={`quick-shop-description-${product.id}`}
              >
                {product.description}
              </p>
              <strong className="quick-shop__price">
                {formatCurrency(product.priceCents)}
              </strong>

              <button
                className="quick-shop__add"
                type="button"
                onClick={() => onAdd(product)}
              >
                <ShoppingBag aria-hidden="true" />
                Add to cart &middot; {formatCurrency(product.priceCents)}
              </button>

              {quantityInCart > 0 && (
                <p className="quick-shop__added" role="status">
                  <Check aria-hidden="true" />
                  {quantityInCart}{' '}
                  {quantityInCart === 1 ? 'item' : 'items'} in your cart
                </p>
              )}
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  )
}
